import { useApolloClient } from '@apollo/client';
import { useState } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { Download01Icon } from '@/components/icons/Download01Icon';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import * as diagnostics from '@/utils/diagnostics';

import {
  FileRepresentation,
  FileRepresentationProps,
} from '../FileRepresentation/FileRepresentation';
import { useDocumentDownload } from '../hooks/useFileDownload';
import { useDeleteDocumentMutation } from './graphql/DocumentRepresentation.generated';

export interface DocumentRepresentationProps extends FileRepresentationProps {
  documentId: string;
  showDelete?: boolean;
  onDelete?: () => void;
}

export function DocumentRepresentation({
  documentId,
  fileName,
  documentType,
  uploadedAt,
  uploadedBy,
  showDelete,
  onDelete,
  sx,
}: DocumentRepresentationProps) {
  const client = useApolloClient();
  const { showFeedback } = useFeedback();
  const [isDocumentDeleted, setDocumentDeleted] = useState(false);
  const [deleteDocument, { loading: isDeletingDocument }] =
    useDeleteDocumentMutation();
  const [downloadFile, { loading: isDownloadingFile }] = useDocumentDownload();

  async function handleRemoveFile() {
    try {
      await deleteDocument({
        variables: {
          documentId: documentId,
        },
        onCompleted: async () => {
          await client.refetchQueries({
            updateCache(cache) {
              cache.evict({ fieldName: 'documents' });
              cache.gc();
            },
          });
        },
      });
      // do this locally to cheaply not show this file.
      setDocumentDeleted(true);
      onDelete && onDelete();
    } catch (err) {
      diagnostics.error(`Failed to delete document`, err as Error, {
        documentId,
      });
      showFeedback(
        'We failed to delete your document. Please refresh the page and try again.'
      );
    }
  }

  if (isDocumentDeleted) return null;
  return (
    <FileRepresentation
      uploadedAt={uploadedAt}
      uploadedBy={uploadedBy}
      fileName={fileName}
      documentType={documentType}
      sx={sx}
      actions={
        <>
          {showDelete && (
            <IconButton
              icon={Trash01Icon}
              ariaLabel="Delete document"
              disabled={isDownloadingFile || isDeletingDocument}
              onClick={handleRemoveFile}
              variant="destructive-transparent"
              size="sm"
            />
          )}
          <IconButton
            icon={Download01Icon}
            ariaLabel="Download document document"
            disabled={isDownloadingFile}
            onClick={() => downloadFile(documentId)}
            variant="secondary"
            size="sm"
          />
        </>
      }
    />
  );
}
